import moment from "moment";

export const unwrapApiList = (value) => {
  const list = value?.$values ?? value;
  return Array.isArray(list) ? list : [];
};

const toNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const normalizePhone = (value) => {
  const phone = String(value ?? "").trim();
  if (phone.startsWith("+90")) return phone.slice(3);
  if (phone.startsWith("90")) return phone.slice(2);
  return phone;
};

const parseRouteDate = (value) => {
  if (!value) return null;

  const parsed = moment(
    value,
    [moment.ISO_8601, "YYYY-MM-DD", "DD.MM.YYYY"],
    true
  );

  return parsed.isValid() ? parsed : null;
};

const getRouteDate = (route, index, field) => {
  const item = route[index] ?? {};
  const directDate = parseRouteDate(item[field]);
  if (directDate) return directDate.format("YYYY-MM-DDTHH:mm:ss");

  if (field === "dateEngec") {
    const startDate = parseRouteDate(item.date);
    if (startDate) return startDate.format("YYYY-MM-DDTHH:mm:ss");
  }

  if (item.locationType === "bosaltma") {
    for (let previousIndex = index - 1; previousIndex >= 0; previousIndex -= 1) {
      const previousDate =
        parseRouteDate(route[previousIndex]?.dateEngec) ??
        parseRouteDate(route[previousIndex]?.date);

      if (previousDate) {
        return previousDate.add(1, "day").format("YYYY-MM-DDTHH:mm:ss");
      }
    }
  }

  return "";
};

const buildRoutePoints = (route, isUpdate) => {
  const routeItems = unwrapApiList(route);

  return routeItems.map((item, index) => {
    const locationData = item?.locationData ?? {};
    const isGoogleAddressSelected = Boolean(locationData.isGoogleAddressSelected);
    const routePoint = {
      city: locationData.il ?? "",
      town: locationData.ilce ?? "",
      address: item?.adres ?? "",
      forDirections: item?.adresTarif ?? "",
      location: locationData.location ?? "",
      order: index + 1,
      loadOrUnload: item?.locationType === "yukleme" ? 0 : 1,
      lat: String(locationData.lat ?? ""),
      lng: String(locationData.lng ?? ""),
      loadDate: getRouteDate(routeItems, index, "date"),
      lateLoadDate: getRouteDate(routeItems, index, "dateEngec"),
      loadTime: item?.saatAllTime ? "00:00" : item?.saatBaslangic || "00:00",
      lateLoadTime: item?.saatAllTime ? "00:00" : item?.saatBitis || "00:00",
      loadAllTime: Boolean(item?.saatAllTime),
      smsVerification: Boolean(item?.smsVerification),
      smsVerificationPhoneNumber: normalizePhone(item?.smsVerificationPhoneNumber),
      routePointPersonName: item?.routePointPersonName ?? "",
      routePointPersonPhoneNumber: normalizePhone(item?.routePointPersonPhoneNumber),
      companyName: item?.companyName ?? "",
      mailAddress: item?.mailAddress ?? "",
      regionId: toNumber(locationData.regionId ?? locationData.bolgeId),
      googleLat: isGoogleAddressSelected ? toNumber(locationData.googleLat) : null,
      googleLng: isGoogleAddressSelected ? toNumber(locationData.googleLng) : null,
      googleAddress:
        locationData.googleLocation ?? locationData.googleSearchText ?? "",
      isGoogleAddressSelected,
      loadMethods: unwrapApiList(item?.sevkBicimleri)
        .filter((method) => method?.status)
        .map((method) => ({
          ...(isUpdate && method?.id != null ? { id: method.id } : {}),
          methodId: toNumber(method?.methodId ?? method?.id),
          name: method?.name ?? "",
        })),
    };

    if (isUpdate && item?.id != null) routePoint.id = item.id;

    if (!isUpdate) {
      routePoint.isAddressSaved = Boolean(item?.adresKaydedilsinMi);
      routePoint.savedAddressName = item?.adresBaslik ?? "";
    }

    return routePoint;
  });
};

const buildPackagingTypes = (packagingData, isUpdate) =>
  unwrapApiList(packagingData).map((item) => ({
    ...(isUpdate && item?.id != null ? { id: item.id } : {}),
    packagingUnitId: toNumber(item?.activePaketlemeTypeId ?? item?.packagingUnitId),
    olculers: unwrapApiList(item?.list ?? item?.olculers).map((dimension) => ({
      ...(isUpdate && dimension?.id != null ? { id: dimension.id } : {}),
      en: toNumber(dimension?.en),
      boy: toNumber(dimension?.boy),
      yukseklik: toNumber(dimension?.yukseklik),
      agirlik: toNumber(dimension?.agirlik),
      sayisi: toNumber(dimension?.sayisi),
      cap: toNumber(dimension?.cap),
    })),
  }));

export const buildRegisteredLoadPayload = ({
  route,
  aracData,
  paymentData,
  yukData,
  paketlemeData,
  routeDistance,
  saveLoadName,
  existingLoad = {},
  publishingTypeId = 1,
  isUpdate = false,
}) => {
  const commissionPaymentId = toNumber(paymentData?.komisyonDurumu?.value);
  const fuelPaymentId = toNumber(paymentData?.yakitDurumu?.value);
  const truckType = aracData?.aracTypeName ?? "";
  const trailerType = aracData?.kasaTypeName ?? "";

  const payload = {
    isParsiyel: Boolean(yukData?.yuklemeTuru?.value),
    loadType: yukData?.yukTuru ?? "",
    isDangerous: Boolean(yukData?.yukADR),
    dangerousDescription: yukData?.yukAdrDescription ?? "",
    loadQuantity:
      toNumber(yukData?.agirlikBirim?.value) === 3
        ? 1
        : toNumber(yukData?.toplamAgirlik),
    loadUnit: yukData?.agirlikBirim?.text ?? "",
    loadUnitId: toNumber(yukData?.agirlikBirim?.value),
    loadBulk: toNumber(yukData?.toplamHacim),
    isStacked: Boolean(yukData?.ustUsteYuklenmesin),
    loadDescription: yukData?.yukDesc ?? "",
    loadValue: toNumber(yukData?.yukunDegeri),
    isInsuranceRequested: Boolean(existingLoad?.isInsuranceRequested),
    truckQuantity: toNumber(aracData?.aracSayisi, 1),
    usedTruckQuantity: toNumber(existingLoad?.usedTruckQuantity),
    truckType,
    truckTypeId: toNumber(aracData?.aracTypeId),
    trailerType,
    trailerTypeId: toNumber(aracData?.kasaTypeId),
    groundType: aracData?.zeminType?.text || "Farketmez",
    groundTypeId: toNumber(aracData?.zeminType?.value),
    truckDescription: aracData?.digerOzellikler ?? "",
    freightVisibility: Boolean(paymentData?.fiyatYayinlansin),
    freight: toNumber(paymentData?.hedefNakliyefiyat),
    commissionPayment: toNumber(paymentData?.komisyonMiktari),
    distance: toNumber(routeDistance),
    publishingTypeId: toNumber(existingLoad?.publishingTypeId ?? publishingTypeId, 1),
    isActive: Boolean(existingLoad?.isActive),
    isFavorite: Boolean(existingLoad?.isFavorite),
    saveLoadName: String(saveLoadName ?? existingLoad?.saveLoadName ?? "").trim(),
    paymentType: paymentData?.odemeSekli?.text ?? "",
    paymentTypeId: toNumber(paymentData?.odemeSekli?.value),
    paymentUnit: toNumber(paymentData?.fiyatlandirmaType?.value),
    commissionPaymentId,
    fuelPaymentId,
    fuelPayment: fuelPaymentId === 0 ? 0 : toNumber(paymentData?.yakitOrani),
    yakıtVarYüzde: 0,
    yakıtVar: false,
    isFuelPayment: fuelPaymentId,
    otherPaymentDescription: paymentData?.odemeSekliDigerAciklama ?? "",
    routePoints: buildRoutePoints(route, isUpdate),
    packingingTypes: buildPackagingTypes(paketlemeData, isUpdate),
  };

  if (isUpdate) {
    payload.id = existingLoad?.id;
    payload.loadOwnerFreight = toNumber(paymentData?.yukverenFiyat);
    payload.companyProjectId = existingLoad?.companyProjectId ?? null;
  } else {
    payload.isLoadSaved = true;
    payload.commissionPaymentType = commissionPaymentId;
    payload.truckName = truckType;
    payload.trailerName = trailerType;
  }

  return payload;
};
